import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  useTheme,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Grid,
  Divider,
  Button,
  Pagination,
  Paper,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import requestsService, {
  RequestListItem,
  RequestsSummary,
  RequestsFilters,
  RequestType,
  RequestStatus,
  SortBy,
} from "../services/requests-service";

interface FiltersState {
  search: string;
  requestType?: RequestType;
  requestStatus?: RequestStatus;
  isUrgent: boolean;
  sortBy: SortBy;
}

const defaultFilters: FiltersState = {
  search: "",
  requestType: undefined,
  requestStatus: undefined,
  isUrgent: false,
  sortBy: "recent",
};

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRequestTypeLabel = (type: RequestType): string => {
  const labels: Record<RequestType, string> = {
    CONSENT_WITHDRAWAL: "Consent Withdrawal",
    DATA_ACCESS: "Data Access",
    DATA_CORRECTION: "Data Correction",
    DATA_ERASURE: "Data Erasure",
    DATA_PORTABILITY: "Data Portability",
  };
  return labels[type] || type;
};

const getRequestStatusLabel = (status: RequestStatus): string => {
  const labels: Record<RequestStatus, string> = {
    PENDING: "Pending",
    IN_PROGRESS: "In Progress",
    COMPLETED: "Completed",
    REJECTED: "Rejected",
    CANCELLED: "Cancelled",
  };
  return labels[status] || status;
};

const getStatusColor = (status: RequestStatus): "default" | "primary" | "success" | "warning" | "error" => {
  switch (status) {
    case "PENDING":
      return "default";
    case "IN_PROGRESS":
      return "primary";
    case "COMPLETED":
      return "success";
    case "REJECTED":
    case "CANCELLED":
      return "error";
    default:
      return "default";
  }
};

const getPriorityColor = (priority: string): "default" | "warning" | "error" => {
  switch (priority) {
    case "URGENT":
      return "error";
    case "HIGH":
      return "warning";
    default:
      return "default";
  }
};

const RequestsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<RequestsSummary>({
    total: 0,
    urgent: 0,
    open: 0,
    closed: 0,
  });
  const [requests, setRequests] = useState<RequestListItem[]>([]);
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageSize = 12;

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const data = await requestsService.getRequestsSummary();
      setSummary(data);
    } catch (err: any) {
      console.error("Error loading summary:", err);
      setSummary({ total: 0, urgent: 0, open: 0, closed: 0 });
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const apiFilters: RequestsFilters = {
        page: currentPage,
        pageSize,
        search: filters.search || undefined,
        requestType: filters.requestType,
        requestStatus: filters.requestStatus,
        isUrgent: filters.isUrgent || undefined,
        sortBy: filters.sortBy,
      };

      const response = await requestsService.listRequests(apiFilters);
      setRequests(response.requests);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (err: any) {
      console.error("Error loading requests:", err);
      setError(err?.message || "Failed to load requests");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleCardClick = (requestId: number) => {
    navigate(`/requests/${requestId}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setCurrentPage(1);
  };

  const handleRequestTypeChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      requestType: value === "all" ? undefined : (value as RequestType),
    }));
    setCurrentPage(1);
  };

  const handleRequestStatusChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      requestStatus: value === "all" ? undefined : (value as RequestStatus),
    }));
    setCurrentPage(1);
  };

  const handleUrgentToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, isUrgent: e.target.checked }));
    setCurrentPage(1);
  };

  const handleSortChange = (e: SelectChangeEvent<string>) => {
    setFilters((prev) => ({ ...prev, sortBy: e.target.value as SortBy }));
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    loadSummary();
    loadRequests();
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterListIcon color="primary" />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {summaryLoading ? <CircularProgress size={24} /> : summary.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Requests
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccessTimeIcon color="primary" />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {summaryLoading ? <CircularProgress size={24} /> : summary.open}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Open Requests
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningAmberIcon color="error" />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                  {summaryLoading ? <CircularProgress size={24} /> : summary.urgent}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Urgent
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon color="success" />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                  {summaryLoading ? <CircularProgress size={24} /> : summary.closed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Closed
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <FilterListIcon color="action" />
          <Typography variant="h6">Filters</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by description or purpose..."
              value={filters.search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Select
              fullWidth
              size="small"
              value={filters.requestType || "all"}
              onChange={handleRequestTypeChange}
              displayEmpty
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="CONSENT_WITHDRAWAL">Consent Withdrawal</MenuItem>
              <MenuItem value="DATA_ACCESS">Data Access</MenuItem>
              <MenuItem value="DATA_CORRECTION">Data Correction</MenuItem>
              <MenuItem value="DATA_ERASURE">Data Erasure</MenuItem>
              <MenuItem value="DATA_PORTABILITY">Data Portability</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Select
              fullWidth
              size="small"
              value={filters.requestStatus || "all"}
              onChange={handleRequestStatusChange}
              displayEmpty
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Select
              fullWidth
              size="small"
              value={filters.sortBy}
              onChange={handleSortChange}
            >
              <MenuItem value="recent">Most Recent</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
              <MenuItem value="status">By Status</MenuItem>
              <MenuItem value="type">By Type</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.isUrgent}
                  onChange={handleUrgentToggle}
                  size="small"
                />
              }
              label="Urgent Only"
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );

  const renderCard = (request: RequestListItem) => (
    <Grid item key={request.requestId} xs={12} sm={6} lg={4}>
      <Card
        onClick={() => handleCardClick(request.requestId)}
        sx={{
          height: "100%",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                {getRequestTypeLabel(request.requestType)}
              </Typography>
              <Chip
                label={getRequestStatusLabel(request.requestStatus)}
                color={getStatusColor(request.requestStatus)}
                size="small"
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
              {request.description || "No description"}
            </Typography>

            {request.purposeName && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Purpose:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {request.purposeName}
                </Typography>
              </Box>
            )}

            <Divider />

            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Priority:
                </Typography>
                <Chip
                  label={request.priority}
                  color={getPriorityColor(request.priority)}
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created:
                </Typography>
                <Typography variant="body2">{formatDate(request.createdDate)}</Typography>
              </Box>

              {request.estimatedCompletionDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Estimated Completion:
                  </Typography>
                  <Typography variant="body2">{formatDate(request.estimatedCompletionDate)}</Typography>
                </Box>
              )}

              {request.actualCompletionDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed:
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                    {formatDate(request.actualCompletionDate)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        My Requests
      </Typography>

      {renderSummaryCards()}
      {renderFilters()}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : requests.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No requests found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filters.search || filters.requestType || filters.requestStatus || filters.isUrgent
              ? "Try adjusting your filters"
              : "You haven't submitted any requests yet"}
          </Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Showing {requests.length} of {totalCount} requests
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {requests.map((request) => renderCard(request))}
          </Grid>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default RequestsPage;

